import { useMemo } from "react";
import {
  DefaultStreamChatGenerics,
  useChannelStateContext,
} from "stream-chat-react";
import { ChannelInfoWrapper } from "../ChannelInfoWrapper";
import * as CHANNEL_TYPES from "constants/channelType";
import {
  ACCOUNT_MANAGER,
  BOOKKEEPER,
  HEAD_OF_SUPPORT,
  INKLE_CEO,
  INKLE_CTO,
  PREPARER,
  REVIEWER,
} from "constants/chatMemberType";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { AvatarGroup } from "components/DesignSystem/AvatarGroup/AvatarGroup";
import classNames from "classnames";
import { Tag, TagColorType } from "components/DesignSystem/Tag/Tag";
import { ChannelMemberResponse } from "stream-chat";

export type TeamMember = {
  id: string;
  image: string;
  name: string;
  roleNames: string[];
};

export const ROLE_TAG_TYPE: Record<string, TagColorType> = {
  [BOOKKEEPER]: "blue",
  [INKLE_CTO]: "orange",
  [INKLE_CEO]: "orange",
  [HEAD_OF_SUPPORT]: "gray",
  [ACCOUNT_MANAGER]: "purple",
  [PREPARER]: "blue",
  [REVIEWER]: "orange",
};

const RANDOM_MEMBERS = [
  { name: "AT", image: "", bgColorClass: "!t-bg-neutral-10" },
  {
    name: "SB",
    image: "",
    bgColorClass: "!t-bg-[#F1F0F3] !t-text-[#767089]",
  },
  {
    name: "RC",
    image: "",
    bgColorClass: "!t-bg-[#F5F5F7] !t-text-[#9C97A9]",
  },
  {
    name: "NP",
    image: "",
    bgColorClass: "!t-bg-surface-lighter-grey !t-text-[#D1CFD8]",
  },
];

export const TeamMembersForCustomer = () => {
  const { channel } = useChannelStateContext();
  const allMembers = useMemo(
    () =>
      (channel?.state?.members ?? {}) as Record<
        string,
        ChannelMemberResponse<DefaultStreamChatGenerics>
      >,
    [channel?.state?.members]
  );
  const assignedAgentId = channel?.data?.assigned_agent_member_id as string;
  const assignedPreparerId = channel?.data
    ?.assigned_preparer_member_id as string;
  const assignedReviewerId = channel?.data
    ?.assigned_reviewer_member_id as string;
  const channelCustomType = channel?.data?.custom_type;
  const isBookChannel =
    channelCustomType === CHANNEL_TYPES.BOOKS_CHANNEL_CUSTOM_TYPE;
  const isFilingChannel = channelCustomType === CHANNEL_TYPES.TASK_CHANNEL;
  const isCompanyGroupChannel =
    channelCustomType === CHANNEL_TYPES.COMPANY_GROUP_CUSTOM_TYPE;

  const teamMembers = useMemo(() => {
    const teamMemberRoles = isCompanyGroupChannel
      ? [ACCOUNT_MANAGER, INKLE_CTO, HEAD_OF_SUPPORT]
      : [
          ACCOUNT_MANAGER,
          ...(isBookChannel ? [BOOKKEEPER] : []),
          ...(isFilingChannel ? [PREPARER] : []),
          HEAD_OF_SUPPORT,
          INKLE_CEO,
          INKLE_CTO,
        ];

    const members = Object.values(allMembers);
    const membersById = new Map(members.map((m) => [m.user?.id as string, m]));
    const teamMemberMap = new Map<
      string,
      {
        member: ChannelMemberResponse<DefaultStreamChatGenerics>;
        roles: string[];
      }
    >();

    const addRole = (userId: string | undefined, role: string) => {
      const member = userId ? membersById.get(userId) : undefined;
      if (!member) return;
      const existing = teamMemberMap.get(userId!);
      if (existing) {
        existing.roles.push(role);
      } else {
        teamMemberMap.set(userId!, { member, roles: [role] });
      }
    };

    const preparerRole = isBookChannel ? BOOKKEEPER : PREPARER;
    addRole(assignedAgentId, ACCOUNT_MANAGER);
    addRole(assignedPreparerId, preparerRole);

    members.forEach((member) => {
      const roleName = member.user?.role_name as string;
      const userId = member.user?.id as string;
      if (userId === assignedReviewerId) return;
      if (roleName === BOOKKEEPER || roleName === PREPARER) return;
      if (!teamMemberRoles.includes(roleName)) return;
      const existing = teamMemberMap.get(userId);
      if (existing) {
        if (!existing.roles.includes(roleName)) existing.roles.push(roleName);
      } else {
        teamMemberMap.set(userId, { member, roles: [roleName] });
      }
    });

    return Array.from(teamMemberMap.entries())
      .map(([userId, { member, roles }]) => ({
        id: userId,
        image: (member.user?.image as string) || "",
        name: (member.user?.name as string) || "",
        roleNames: roles,
      }))
      .sort(
        (a, b) =>
          teamMemberRoles.indexOf(a.roleNames[0]) -
          teamMemberRoles.indexOf(b.roleNames[0])
      );
  }, [
    allMembers,
    assignedAgentId,
    assignedPreparerId,
    assignedReviewerId,
    isBookChannel,
    isCompanyGroupChannel,
    isFilingChannel,
  ]);

  return (
    <ChannelInfoWrapper
      title={
        <div className="t-text-subtext t-flex t-gap-1 t-items-center">
          Team Members
        </div>
      }
      hasDetails={false}
    >
      <div className="t-flex t-flex-col t-gap-3">
        <div className="t-flex t-flex-col t-gap-3 t-p-2">
          {teamMembers.map((teamMember) => (
            <div
              className={classNames(
                { "t-items-start": teamMember.roleNames.length > 1 },
                { "t-items-center": teamMember.roleNames.length <= 1 },
                "t-flex t-gap-2 t-justify-between"
              )}
              key={teamMember.id}
            >
              <div className="t-flex t-items-center t-gap-2">
                <Avatar
                  lettersCount={2}
                  size="regular"
                  src={teamMember.image}
                  alt={teamMember.name}
                  className="t-w-8 t-h-8 t-rounded-full"
                />
                <p className="t-m-0 t-text-subtext">{teamMember.name}</p>
              </div>
              {teamMember.roleNames.length > 0 && (
                <span className="t-flex t-flex-col t-gap-1 t-shrink-0 t-items-end">
                  {teamMember.roleNames.map((role) => (
                    <Tag
                      key={role}
                      tagType={ROLE_TAG_TYPE[role] || "gray"}
                      size="small"
                      icon={false}
                    >
                      {role}
                    </Tag>
                  ))}
                </span>
              )}
            </div>
          ))}
          <div className="t-flex t-items-center t-gap-x-1">
            <AvatarGroup max={4} size="small">
              {RANDOM_MEMBERS.map((member) => (
                <Avatar
                  key={member.name}
                  alt={member.name}
                  src={member.image}
                  size="small"
                  lettersCount={2}
                  className={classNames(
                    "t-ring-2 t-ring-white !t-text-[7px]",
                    member.bgColorClass || ""
                  )}
                />
              ))}
            </AvatarGroup>
            <span className="t-text-body-sm-italic t-italic t-text-text-30">
              and other Inkle team members
            </span>
          </div>
        </div>
      </div>
    </ChannelInfoWrapper>
  );
};
